import { useEffect, useRef, useState } from "react";
import { ExternalLink, FileText, Link as LinkIcon, Download } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { plainError } from "@/lib/plain-error";
import {
  attachmentLabel, describeLink, fileProblem, formatBytes, kindForFile, linkProblem,
  storagePathFor, type Attachment,
} from "@/lib/attachments";

/**
 * What is attached to this card.
 *
 * Three kinds, because a card is about work and work has three shapes: the
 * screenshot of the thing that is wrong, the file somebody produced, and the
 * Drive document everybody is actually working out of.
 *
 * A Drive document is LINKED, never copied. It has its own permissions, its own
 * version history and somebody editing it right now — a copy in our bucket is
 * wrong within a day and bypasses whatever Drive was told about who may read it.
 */

const BUCKET = "task-attachments";

export function TaskAttachments({
  taskId, employeeId, canEdit = true,
}: { taskId: string; employeeId: string | undefined; canEdit?: boolean }) {
  const [items, setItems] = useState<Attachment[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<null | "link">(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");

  const imageRef = useRef<HTMLInputElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    void (async () => {
      const { data, error: e } = await supabase.from("hq_task_attachments")
        .select("*").eq("task_id", taskId).order("created_at");
      if (e) { setError(plainError(e, "load what's attached")); return; }
      setItems((data ?? []) as Attachment[]);
    })();
  }, [taskId]);

  // The bucket is private, so anything we hold renders through a short-lived
  // signed URL. A link needs none — we do not hold it.
  useEffect(() => {
    for (const a of items) {
      if (!a.storage_path || urls[a.id]) continue;
      void supabase.storage.from(BUCKET).createSignedUrl(a.storage_path, 3600).then(({ data }) => {
        if (data?.signedUrl) setUrls(prev => ({ ...prev, [a.id]: data.signedUrl }));
      });
    }
  }, [items, urls]);

  async function addFile(file: File) {
    if (!employeeId) return;
    const problem = fileProblem(file);
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError(null);
    const path = storagePathFor(taskId, file.name);
    const { error: upErr } = await supabase.storage.from(BUCKET)
      .upload(path, file, { contentType: file.type || "application/octet-stream" });
    if (upErr) { setBusy(false); setError(plainError(upErr, "upload that")); return; }

    const { data, error: e } = await supabase.from("hq_task_attachments").insert({
      task_id: taskId, kind: kindForFile(file.type), storage_path: path,
      file_name: file.name, file_type: file.type || null, file_size: file.size,
      uploaded_by: employeeId,
    }).select().single();
    setBusy(false);
    if (e) {
      // The file is in the bucket and the row is not. Take the file back out,
      // or it sits there forever with nothing pointing at it.
      await supabase.storage.from(BUCKET).remove([path]);
      setError(plainError(e, "attach that"));
      return;
    }
    setItems(prev => [...prev, data as Attachment]);
  }

  // The card catches the paste — it owns the modal that has focus — and hands
  // the file here, because this is where uploading lives. An event rather than
  // a prop so the card does not have to hold a ref into this component.
  useEffect(() => {
    const onAttach = (e: Event) => {
      const detail = (e as CustomEvent<{ taskId: string; file: File }>).detail;
      if (detail?.taskId !== taskId || !detail.file) return;
      void addFile(detail.file);
    };
    window.addEventListener("hq:attach-file", onAttach);
    return () => window.removeEventListener("hq:attach-file", onAttach);
    // addFile closes over employeeId and taskId, which is all it needs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, employeeId]);

  async function addLink() {
    if (!employeeId) return;
    const problem = linkProblem(linkUrl);
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError(null);
    const { data, error: e } = await supabase.from("hq_task_attachments").insert({
      task_id: taskId, kind: "link", link_url: linkUrl.trim(),
      title: linkTitle.trim() || null, uploaded_by: employeeId,
    }).select().single();
    setBusy(false);
    if (e) { setError(plainError(e, "attach that link")); return; }
    setItems(prev => [...prev, data as Attachment]);
    setLinkUrl(""); setLinkTitle(""); setAdding(null);
  }

  async function remove(a: Attachment) {
    setError(null);
    const { error: e } = await supabase.from("hq_task_attachments").delete().eq("id", a.id);
    if (e) { setError(plainError(e, "remove that")); return; }
    // Only after the row is gone: a file with no row is invisible, a row with no
    // file is a broken thumbnail nobody can explain.
    if (a.storage_path) await supabase.storage.from(BUCKET).remove([a.storage_path]);
    setItems(prev => prev.filter(x => x.id !== a.id));
  }

  const screenshots = items.filter(a => a.kind === "screenshot");
  const rest = items.filter(a => a.kind !== "screenshot");

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="label">Attachments</label>
        {canEdit && (
          <div className="flex items-center gap-3 text-xs">
            <button onClick={() => imageRef.current?.click()} disabled={busy}
              className="text-teal-dark hover:underline disabled:opacity-50">
              {busy ? "Working…" : "+ Screenshot"}
            </button>
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="text-teal-dark hover:underline disabled:opacity-50">+ File</button>
            <button onClick={() => { setAdding(adding === "link" ? null : "link"); setError(null); }}
              aria-expanded={adding === "link"}
              className="text-teal-dark hover:underline">+ Drive link</button>
          </div>
        )}
      </div>

      {/* Driven by the buttons above, so they carry the visible name — but they
          are still in the accessibility tree, and an unnamed file input is
          announced as "button" with nothing after it. */}
      <input ref={imageRef} type="file" accept="image/*" className="hidden"
        aria-label="Choose a screenshot to attach"
        onChange={e => { const f = e.target.files?.[0]; if (f) void addFile(f); e.target.value = ""; }} />
      <input ref={fileRef} type="file" className="hidden"
        aria-label="Choose a file to attach"
        onChange={e => { const f = e.target.files?.[0]; if (f) void addFile(f); e.target.value = ""; }} />

      {error && <p className="mt-1 text-xs text-bad">{error}</p>}

      {adding === "link" && (
        <div className="mt-2 space-y-2 rounded-lg border border-line bg-offwhite p-2.5">
          <div>
            <label htmlFor={`link-url-${taskId}`} className="label">Link</label>
            <input id={`link-url-${taskId}`} className="input py-1 text-sm" autoFocus
              placeholder="https://docs.google.com/…"
              value={linkUrl} onChange={e => { setLinkUrl(e.target.value); setError(null); }}
              onKeyDown={e => { if (e.key === "Enter") void addLink(); if (e.key === "Escape") setAdding(null); }} />
            {linkUrl.trim() && !linkProblem(linkUrl) && (
              <p className="mt-1 text-[11px] text-gray2">{describeLink(linkUrl.trim()).label}</p>
            )}
          </div>
          <div>
            <label htmlFor={`link-title-${taskId}`} className="label">What is it? (optional)</label>
            <input id={`link-title-${taskId}`} className="input py-1 text-sm"
              placeholder="Q3 pricing sheet"
              value={linkTitle} onChange={e => setLinkTitle(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") void addLink(); if (e.key === "Escape") setAdding(null); }} />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setAdding(null); setLinkUrl(""); setLinkTitle(""); setError(null); }}
              className="btn btn-sm btn-ghost">Cancel</button>
            <button onClick={() => void addLink()} disabled={busy} className="btn btn-sm">Attach</button>
          </div>
          {/* Said once, where somebody is about to paste a Drive URL. */}
          <p className="text-[11px] text-gray2">
            The document stays in Drive — HQ links to it rather than copying it, so its permissions
            and its history stay where they are. Anybody who opens it needs access in Drive.
          </p>
        </div>
      )}

      {items.length === 0 && adding !== "link" && (
        <p className="mt-1 text-xs text-gray2">
          Nothing attached. Use the buttons, or paste an image anywhere in this card.
        </p>
      )}

      {screenshots.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {screenshots.map(a => (
            <div key={a.id} className="group relative">
              {urls[a.id] ? (
                <a href={urls[a.id]} target="_blank" rel="noreferrer" title={attachmentLabel(a)}>
                  <img src={urls[a.id]} alt={attachmentLabel(a)}
                    className="h-24 w-32 rounded-lg border border-line object-cover" />
                </a>
              ) : (
                <div className="flex h-24 w-32 items-center justify-center rounded-lg border border-line bg-offwhite text-xs text-gray2">…</div>
              )}
              {canEdit && (
                <button onClick={() => void remove(a)} title={`Remove ${attachmentLabel(a)}`}
                  aria-label={`Remove ${attachmentLabel(a)}`}
                  className="absolute -right-1.5 -top-1.5 hidden h-5 w-5 items-center justify-center rounded-full bg-bad text-[11px] text-white shadow group-hover:flex">×</button>
              )}
            </div>
          ))}
        </div>
      )}

      {rest.length > 0 && (
        <ul className="mt-2 space-y-1">
          {rest.map(a => {
            const isLink = a.kind === "link";
            const d = isLink && a.link_url ? describeLink(a.link_url) : null;
            const href = isLink ? a.link_url ?? "#" : urls[a.id];
            const Icon = isLink ? (d?.source === "web" ? LinkIcon : ExternalLink) : FileText;
            return (
              <li key={a.id} className="flex items-center gap-2 rounded-lg border border-line px-2 py-1.5">
                <Icon size={14} className="shrink-0 text-gray2" aria-hidden="true" />
                <a href={href} target="_blank" rel="noreferrer"
                   className={`min-w-0 flex-1 truncate text-sm hover:underline ${href ? "" : "pointer-events-none opacity-60"}`}
                   title={isLink ? a.link_url ?? "" : a.file_name ?? ""}>
                  {attachmentLabel(a)}
                </a>
                <span className="shrink-0 text-[11px] text-gray2">
                  {isLink ? d?.label : [a.file_type?.split("/").pop(), a.file_size ? formatBytes(a.file_size) : null]
                    .filter(Boolean).join(" · ")}
                </span>
                {!isLink && href && (
                  <a href={href} download={a.file_name ?? undefined}
                     aria-label={`Download ${attachmentLabel(a)}`}
                     className="shrink-0 text-gray2 hover:text-teal-dark">
                    <Download size={13} aria-hidden="true" />
                  </a>
                )}
                {canEdit && (
                  <button onClick={() => void remove(a)}
                    aria-label={`Remove ${attachmentLabel(a)}`}
                    className="shrink-0 text-gray2 hover:text-bad">×</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

